import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Edit, Trash2, Plus } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { DatePicker } from "@/components/ui/date-picker";

export default function TimeTracking() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [filter, setFilter] = useState({ user_id: "all", project_id: "all", start_date: "", end_date: "" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", project_id: "", clock_in: "", clock_out: "", note_type: "regular", note: "" });
  const [editing, setEditing] = useState(null);

  const reload = async () => {
    const params = {};
    if (filter.user_id && filter.user_id !== "all") params.user_id = filter.user_id;
    if (filter.project_id && filter.project_id !== "all") params.project_id = filter.project_id;
    if (filter.start_date) params.start_date = filter.start_date;
    if (filter.end_date) params.end_date = filter.end_date;
    const [e, p, u] = await Promise.all([
      api.get("/time/entries", { params }),
      api.get("/projects"),
      user.role === "admin" ? api.get("/users") : Promise.resolve({ data: [] }),
    ]);
    setEntries(e.data);
    setProjects(p.data);
    setEmployees(u.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter.user_id, filter.project_id, filter.start_date, filter.end_date]);

  const projectName = (id) => projects.find((p) => p.id === id)?.name || "—";

  const submit = async () => {
    try {
      if (editing) {
        await api.patch(`/time/entries/${editing}`, form);
        toast.success("Updated");
      } else {
        await api.post("/time/entries", form);
        toast.success("Entry created");
      }
      setOpen(false); setEditing(null);
      setForm({ user_id: "", project_id: "", clock_in: "", clock_out: "", note_type: "regular", note: "" });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const approve = async (id, approved) => {
    try {
      await api.patch(`/time/entries/${id}`, { approved });
      toast.success(approved ? "Approved" : "Unapproved");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/time/entries/${id}`); reload(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (e) => {
    setEditing(e.id);
    setForm({
      user_id: e.user_id, project_id: e.project_id,
      clock_in: (e.clock_in || "").slice(0, 16), clock_out: (e.clock_out || "").slice(0, 16),
      note_type: e.note_type || "regular", note: e.note || "",
    });
    setOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="time-tracking-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">TIME TRACKING</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Timesheets</h1>
        </div>
        {user.role === "admin" && (
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="new-time-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">
                <Plus className="h-4 w-4 mr-2" /> Manual Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-none text-white">
              <DialogHeader><DialogTitle className="font-heading uppercase">{editing ? "Edit Time" : "Manual Time Entry"}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {!editing && (
                  <div>
                    <Label className="label-eyebrow">EMPLOYEE</Label>
                    <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                      <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue placeholder="Select…" /></SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                        {employees.filter((e) => e.role === "employee").map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div>
                  <Label className="label-eyebrow">PROJECT</Label>
                  <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                    <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                      {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-eyebrow">CLOCK IN</Label>
                    <Input type="datetime-local" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" />
                  </div>
                  <div>
                    <Label className="label-eyebrow">CLOCK OUT</Label>
                    <Input type="datetime-local" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="label-eyebrow">TYPE</Label>
                  <Select value={form.note_type} onValueChange={(v) => setForm({ ...form, note_type: v })}>
                    <SelectTrigger className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                      <SelectItem value="regular">Regular</SelectItem>
                      <SelectItem value="overtime">Overtime</SelectItem>
                      <SelectItem value="travel">Travel</SelectItem>
                      <SelectItem value="standby">Standby</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-eyebrow">NOTE</Label>
                  <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="bg-black border-[#222] rounded-none mt-2 font-mono text-sm" rows={2} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">{editing ? "Update" : "Create"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filters */}
      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] p-4 flex flex-wrap gap-3 items-end">
        {user.role === "admin" && (
          <div className="min-w-[180px]">
            <Label className="label-eyebrow">EMPLOYEE</Label>
            <Select value={filter.user_id || "all"} onValueChange={(v) => setFilter({ ...filter, user_id: v })}>
              <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue placeholder="All" /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                <SelectItem value="all">All employees</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="min-w-[180px]">
          <Label className="label-eyebrow">PROJECT</Label>
          <Select value={filter.project_id || "all"} onValueChange={(v) => setFilter({ ...filter, project_id: v })}>
            <SelectTrigger className="bg-black border-[#222] rounded-none h-10 mt-2 font-mono"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
              <SelectItem value="all">All projects</SelectItem>
              {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-eyebrow">FROM</Label>
          <div className="mt-2"><DatePicker value={filter.start_date} onChange={(v) => setFilter({ ...filter, start_date: v })} /></div>
        </div>
        <div>
          <Label className="label-eyebrow">TO</Label>
          <div className="mt-2"><DatePicker value={filter.end_date} onChange={(v) => setFilter({ ...filter, end_date: v })} /></div>
        </div>
      </Card>

      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-x-auto">
        <table className="w-full text-sm" data-testid="time-table">
          <thead>
            <tr className="text-[#666] label-eyebrow border-b border-[#1c1c1c]">
              <Th>DATE</Th><Th>EMPLOYEE</Th><Th>PROJECT</Th><Th>IN</Th><Th>OUT</Th><Th align="right">HOURS</Th><Th>TYPE</Th><Th>STATUS</Th>{user.role === "admin" && <Th>ACTIONS</Th>}
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-[#1c1c1c] hover:bg-[#101010]">
                <Td mono>{(e.clock_in || "").slice(0, 10)}</Td>
                <Td>{e.user_name}</Td>
                <Td>{projectName(e.project_id)}</Td>
                <Td mono>{(e.clock_in || "").slice(11, 16)}</Td>
                <Td mono>{e.clock_out ? e.clock_out.slice(11, 16) : <span className="text-red-400">OPEN</span>}</Td>
                <Td mono align="right">{e.hours?.toFixed?.(2) ?? e.hours}</Td>
                <Td><span className="label-eyebrow">{e.note_type || "regular"}</span></Td>
                <Td>
                  {e.approved
                    ? <Badge className="rounded-none bg-emerald-500/10 text-emerald-400 border-emerald-500/30 font-mono text-xs">APPROVED</Badge>
                    : <Badge variant="outline" className="rounded-none border-[#333] font-mono text-xs">PENDING</Badge>}
                </Td>
                {user.role === "admin" && (
                  <Td>
                    <div className="flex gap-1">
                      {!e.approved && <Button size="sm" variant="ghost" onClick={() => approve(e.id, true)} className="rounded-none h-7 px-2 text-emerald-400"><Check className="h-3 w-3" /></Button>}
                      <Button size="sm" variant="ghost" onClick={() => startEdit(e)} className="rounded-none h-7 px-2"><Edit className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(e.id)} className="rounded-none h-7 px-2 text-red-400"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </Td>
                )}
              </tr>
            ))}
            {entries.length === 0 && <tr><td colSpan={9} className="text-center py-10 font-mono text-[#666]">NO ENTRIES</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

const Th = ({ children, align }) => <th className={`px-4 py-3 font-normal ${align === "right" ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, mono, align }) => <td className={`px-4 py-3 ${mono ? "font-mono tabular" : ""} ${align === "right" ? "text-right" : ""}`}>{children}</td>;
